import React, { useEffect, useMemo, useReducer, useState } from "react";
import "./DictionaryDetails.scss";

import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { SidebarItemType, DictionarySidebarItem } from "src/types";
import { AddedBy } from "src/app/Task/components/AddedBy/AddedBy";
import { convertToLocalTimezone } from "src/app/methods/convertToLocalTimezone";
import { useQuery } from "src/app/methods/useQuery";
import { useShowPane } from "src/hooks/useShowPane";
import { ReactComponent as CloseIcon } from "src/images/cross-alt.svg";
import {
  clearDictionaryDetails,
  getDictionaryDetails,
  setSelectedTableView,
  deleteDictionaryElement,
} from "src/redux";
import { RootState } from "src/redux/reducers";
import dictionaryFiltersReducer, {
  dictionaryFiltersReducerInitialState,
} from "src/app/Dictionary/redux/dictionaryFiltersReducer";
import TableViewSelector from "src/app/TableView/TableViewSelector";
import { getSelectDataSetForColumns } from "src/utils/methods";
import { Breadcrumbs, BreadcrumbsItem } from "../Breadcrumbs/Breadcrumbs";
import { Pane } from "../Pane/Pane";
import PreviewInfo from "../Preview/components/PreviewInfo";
import { PreviewContext } from "../Preview/utils";
import DictionaryMetadata from "./components/DictionaryMetadata/DictionaryMetadata";
import { closeDictionaryDetails } from "./methods";
import {
  setDictionaryFilters,
  setDictionarySort,
  setDictionarySortBy,
  setHiddenDictionaryColumnIds,
} from "../DictionaryFilters/redux/dictionaryFiltersActions";
import { PreviewSettingsDropdown } from "../GlobalTaskDetails/PreviewSettingsDropdown/PreviewSettingsDropdown";
import { usePageTitle } from "src/app/title-management/usePageTitle";

export default function DictionaryDetails(props: any) {
  const { tableViewsFunctions } = props;

  const {
    mainReducer: { activeWorkspaceUuid, identity },
    dictionaryReducer: { dictionaryDetails, dictionaryList },
    projectReducer: { membersList },
  } = useSelector((state: RootState) => state);

  const query = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const params = useParams<{ dictionaryUuid: string }>();

  const dictionaryUuidFromParams = params.dictionaryUuid;
  const dictionaryElementUuid = query.get("displayDictionaryPreview") || "";
  const additionalDictionaryUuid = query.get("additionalDictionaryUuid") || "";
  const dictionaryUuid = dictionaryUuidFromParams || additionalDictionaryUuid;

  const [showPane, hidePane] = useShowPane(() => {
    closeDictionaryDetails(history);
  });

  const [dateCreated, setDateCreated] = useState<moment.Moment | null>(null);
  const [dictionaryFilters, dispatchDF] = useReducer(
    dictionaryFiltersReducer,
    dictionaryFiltersReducerInitialState(dictionaryList.columns),
  );

  const filters = tableViewsFunctions || dictionaryFilters;

  const {
    hiddenColumns,
    dictionaryColumnRanks,
    selectedTableView = {
      label: "Custom",
      value: null,
    },
  } = filters;

  const { wsTableViews } = dictionaryList;

  const taskOwner = useMemo(() => {
    return membersList?.find(
      (member) => member.id === dictionaryDetails?.ownerWsMemberUuid,
    );
  }, [dictionaryDetails?.ownerWsMemberUuid, membersList]);

  const foundDictionarySidebarItem = identity?.sidebarItems?.find(
    (item) =>
      item.type === SidebarItemType.Dictionary &&
      item.wsDictionaryUuid === dictionaryDetails.wsDictionaryUuid,
  ) as DictionarySidebarItem | undefined;

  const header = foundDictionarySidebarItem?.wsDictionaryName;

  usePageTitle(
    dictionaryDetails?.title &&
      header &&
      `${dictionaryDetails?.title} - ${header}`,
  );

  useEffect(() => {
    const cols = dictionaryDetails?.metadata?.map((item) => ({
      ...item,
      options: item.data,
    }));

    if (cols?.length) {
      getSelectDataSetForColumns(dispatch, cols);
    }
  }, [dictionaryDetails?.metadata]);

  useEffect(() => {
    dispatch(getDictionaryDetails(dictionaryUuid, dictionaryElementUuid));
    return () => {
      dispatch(clearDictionaryDetails());
    };
  }, [dictionaryUuid, dictionaryElementUuid]);

  useEffect(() => {
    if (dictionaryDetails?.created) {
      const dateValue = dictionaryDetails.created.date;
      if (dateValue !== null) {
        setDateCreated(convertToLocalTimezone(dateValue));
      }
    }
  }, [dictionaryDetails?.created]);

  const filteredMetadata = useMemo(
    () =>
      dictionaryDetails.metadata
        ?.filter((col) => !hiddenColumns?.includes(col.uuid))
        .sort((a, b) => {
          const rankA = dictionaryColumnRanks?.find(
            (col: { uuid: string }) => col.uuid === a.uuid,
          )?.rank;
          const rankB = dictionaryColumnRanks?.find(
            (col: { uuid: string }) => col.uuid === b.uuid,
          )?.rank;

          if (rankA && rankB) return rankA.localeCompare(rankB);
          return 0;
        }) || [],
    [dictionaryDetails.metadata, hiddenColumns, dictionaryColumnRanks],
  );

  return (
    <Pane
      className="dictionary-details"
      backdropClassName="dictionary-details__backdrop"
      showPane={showPane}
      hidePane={hidePane}
    >
      {/* TODO FRONT: mamy juz enumy i klasy do linkow to mozna korzystac :) https://indahash.slack.com/archives/G4R8J0ZFU/p1695193798225969 */}
      <div className="dictionary-details__topbar">
        <Breadcrumbs>
          <BreadcrumbsItem
            text={header}
            to={`/workspace/${activeWorkspaceUuid}/dict/${header?.toLowerCase()}/${
              dictionaryDetails.wsDictionaryUuid
            }`}
          />
          <BreadcrumbsItem
            text={dictionaryDetails?.title}
            to={`/workspace/${activeWorkspaceUuid}/dict/${header?.toLowerCase()}/${
              dictionaryDetails.wsDictionaryUuid
            }?displayDictionaryPreview=${dictionaryElementUuid}`}
          />
        </Breadcrumbs>

        <div className="dictionary-details__topbar-buttons">
          <PreviewSettingsDropdown
            removeData={{
              objectNames: [dictionaryDetails.title],
              removeFunction: () => {
                closeDictionaryDetails(history);
                dispatch(
                  deleteDictionaryElement(dictionaryUuid, [
                    dictionaryElementUuid,
                  ]),
                );
              },
            }}
          />

          <CloseIcon
            onClick={hidePane}
            className="dictionary-details__topbar-close-icon"
          />
        </div>
      </div>

      <div className="dictionary-details__content">
        <div className="dictionary-details__content-wrapper">
          <PreviewInfo
            context={PreviewContext.Dictionary}
            previewObjectUuid={dictionaryDetails.uuid}
            title={dictionaryDetails.title}
            titleMetaFieldUuid={dictionaryDetails?.titleMetaFieldUuid}
          />

          <TableViewSelector
            wsTableViews={wsTableViews}
            selectedWsTableView={selectedTableView}
            setSelectedWsTableView={(newValue: any) =>
              // @ts-ignore
              dispatchDF(setSelectedTableView(newValue))
            }
            setFilters={(newFilters: any, skipLS: boolean) =>
              // @ts-ignore
              dispatchDF(setDictionaryFilters(newFilters, skipLS))
            }
            setHiddenOrVisibleColumnIds={(columnIds: any, skipLS: boolean) =>
              // @ts-ignore
              dispatchDF(setHiddenDictionaryColumnIds(columnIds, skipLS))
            }
            setSortBy={(newValue: any, skipLS: boolean) =>
              // @ts-ignore
              dispatchDF(setDictionarySortBy(newValue, skipLS))
            }
            setSort={(newValue: any, skipLS: boolean) =>
              // @ts-ignore
              dispatchDF(setDictionarySort(newValue, skipLS))
            }
            columnIds={dictionaryList?.columns?.map((field) => field.uuid)}
            localStorageKey="ws-dictionary-settings"
            {...tableViewsFunctions}
          />
        </div>

        <DictionaryMetadata
          elementUuid={dictionaryElementUuid}
          metadata={filteredMetadata}
        />

        {taskOwner && (
          <AddedBy
            name={taskOwner.name}
            avatarUrl={taskOwner.avatarUrl}
            dateCreated={dateCreated}
          />
        )}
      </div>
    </Pane>
  );
}
